import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Star, CheckCircle2, ChevronRight, ChevronLeft, 
  Send, LogOut, Clock, MessageSquare, ListChecks, FolderOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { surveyAPI } from '../lib/api';
import LanguageSelector from '../components/LanguageSelector';

const EmployeeSurvey = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');

  useEffect(() => {
    loadSurveys();
  }, []);

  const loadSurveys = async () => {
    try {
      const response = await surveyAPI.getAvailable();
      setSurveys(response.data);
    } catch (error) {
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const loadSurveyDetails = async (surveyId) => {
    setLoading(true);
    try {
      const response = await surveyAPI.getSurveyForEmployee(surveyId);
      setSelectedSurvey(response.data);
      
      // Load draft if exists
      const draftResponse = await surveyAPI.getDraft(surveyId);
      if (draftResponse.data.has_draft) {
        setAnswers(draftResponse.data.answers);
        toast.info('Your previous progress has been restored');
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You have already completed this survey');
        setSurveys(surveys.map(s => 
          s.id === surveyId ? { ...s, is_completed: true } : s
        ));
      } else {
        toast.error('Failed to load survey');
      }
    } finally {
      setLoading(false);
    }
  };

  const autoSave = useCallback(async () => {
    if (!selectedSurvey || Object.keys(answers).length === 0) return;
    
    setAutoSaveStatus('saving');
    try {
      await surveyAPI.autoSave(selectedSurvey.id, {
        survey_id: selectedSurvey.id,
        answers: Object.entries(answers).map(([question_id, answer]) => ({
          question_id,
          answer
        }))
      });
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(''), 2000);
    } catch (error) {
      setAutoSaveStatus('error');
    }
  }, [selectedSurvey, answers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.keys(answers).length > 0) {
        autoSave();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [answers, autoSave]);

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    // Validate required questions
    const requiredQuestions = selectedSurvey.questions.filter(q => q.is_required);
    const isEmpty = (q) => {
      const v = answers[q.id];
      if (v === undefined || v === null || v === '') return true;
      if (Array.isArray(v) && v.length === 0) return true;
      return false;
    };
    const missingRequired = requiredQuestions.filter(isEmpty);
    
    if (missingRequired.length > 0) {
      toast.error(`Please answer all required questions (${missingRequired.length} remaining)`);
      return;
    }

    // Validate multi_select min selections
    for (const q of selectedSurvey.questions) {
      if (q.type === 'multi_select') {
        const v = answers[q.id];
        if (Array.isArray(v) && v.length > 0 && q.min_selections && v.length < q.min_selections) {
          toast.error(`"${q.text}" requires at least ${q.min_selections} selection${q.min_selections > 1 ? 's' : ''}`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      await surveyAPI.submit(selectedSurvey.id, {
        survey_id: selectedSurvey.id,
        answers: Object.entries(answers).map(([question_id, answer]) => ({
          question_id,
          answer
        }))
      });
      toast.success('Survey submitted successfully!');
      navigate('/survey/complete');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const questions = selectedSurvey?.questions || [];
  const progress = questions.length > 0 
    ? ((currentQuestion + 1) / questions.length) * 100 
    : 0;

  // Group questions by section and get section list
  const { sections, sectionMap } = useMemo(() => {
    if (!questions.length) return { sections: [], sectionMap: {} };
    
    const map = {};
    questions.forEach((q, idx) => {
      const section = q.section || 'General';
      if (!map[section]) map[section] = [];
      map[section].push({ ...q, globalIndex: idx });
    });
    
    return { sections: Object.keys(map), sectionMap: map };
  }, [questions]);

  // Get current section based on current question
  const currentSection = useMemo(() => {
    if (!questions.length) return null;
    return questions[currentQuestion]?.section || 'General';
  }, [questions, currentQuestion]);

  const renderQuestion = (question) => {
    const sLabels = selectedSurvey?.section_labels || {};
    const currentLabels = sLabels[question.section || 'General'] || {};
    const hasCustomLabels = Object.values(currentLabels).some(v => v);

    switch (question.type) {
      case 'rating':
        return (
          <div className="space-y-4 sm:space-y-6">
            {hasCustomLabels && (
              <div className="flex flex-wrap justify-center gap-2 mb-2">
                {[1,2,3,4,5].map(n => (
                  currentLabels[String(n)] ? (
                    <span key={n} className="text-xs bg-muted px-2 py-1 rounded">
                      <strong>{n}</strong> = {currentLabels[String(n)]}
                    </span>
                  ) : null
                ))}
              </div>
            )}
            <div className="flex justify-center gap-2 sm:gap-3">
              {[1, 2, 3, 4, 5].map((rating) => (
                <div key={rating} className="flex flex-col items-center gap-1">
                  <button
                    onClick={() => handleAnswer(question.id, rating)}
                    data-testid={`rating-${rating}`}
                    className={`p-2.5 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 transform hover:scale-110 ${
                      answers[question.id] === rating
                        ? 'bg-gradient-to-br from-secondary to-secondary/80 text-white scale-110 shadow-lg shadow-secondary/30'
                        : answers[question.id] >= rating
                        ? 'bg-secondary/20 text-secondary'
                        : 'bg-muted hover:bg-secondary/10'
                    }`}
                  >
                    <Star 
                      className={`h-7 w-7 sm:h-10 sm:w-10 ${
                        answers[question.id] >= rating 
                          ? 'fill-current' 
                          : ''
                      }`} 
                    />
                  </button>
                  {hasCustomLabels && currentLabels[String(rating)] && (
                    <span className="text-[10px] text-muted-foreground text-center max-w-16 leading-tight hidden sm:block">
                      {currentLabels[String(rating)]}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-sm text-muted-foreground px-2">
              <span>{hasCustomLabels && currentLabels['1'] ? currentLabels['1'] : 'Not Satisfied'}</span>
              <span>{hasCustomLabels && currentLabels['5'] ? currentLabels['5'] : 'Very Satisfied'}</span>
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6">
            <button
              type="button"
              onClick={() => handleAnswer(question.id, 'yes')}
              data-testid="yes-option"
              className={`flex items-center justify-center gap-2 sm:gap-3 px-8 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                answers[question.id] === 'yes'
                  ? 'border-accent bg-gradient-to-br from-accent to-accent/80 text-white shadow-lg shadow-accent/30'
                  : 'border-border hover:border-accent/50 hover:bg-accent/5'
              }`}
            >
              <CheckCircle2 className="h-5 w-5 sm:h-7 sm:w-7" />
              <span className="text-base sm:text-lg font-semibold">Yes</span>
            </button>
            <button
              type="button"
              onClick={() => handleAnswer(question.id, 'no')}
              data-testid="no-option"
              className={`flex items-center justify-center gap-2 sm:gap-3 px-8 sm:px-10 py-4 sm:py-5 rounded-xl sm:rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                answers[question.id] === 'no'
                  ? 'border-destructive bg-gradient-to-br from-destructive to-destructive/80 text-white shadow-lg shadow-destructive/30'
                  : 'border-border hover:border-destructive/50 hover:bg-destructive/5'
              }`}
            >
              <span className="h-5 w-5 sm:h-7 sm:w-7 flex items-center justify-center text-lg sm:text-xl">✕</span>
              <span className="text-base sm:text-lg font-semibold">No</span>
            </button>
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-3 sm:space-y-4 max-w-lg mx-auto">
            {question.options?.map((option, idx) => (
              <div
                key={option.id}
                className={`flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                  answers[question.id] === option.value
                    ? 'border-secondary bg-gradient-to-r from-secondary/10 to-transparent shadow-md'
                    : 'border-border hover:border-secondary/50 hover:bg-secondary/5'
                }`}
                onClick={() => handleAnswer(question.id, option.value)}
                data-testid={`option-${option.value}`}
              >
                <div className={`h-5 w-5 sm:h-6 sm:w-6 rounded-full border-2 flex items-center justify-center transition-all ${
                  answers[question.id] === option.value
                    ? 'border-secondary bg-secondary'
                    : 'border-muted-foreground'
                }`}>
                  {answers[question.id] === option.value && (
                    <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                  )}
                </div>
                <span className="text-sm sm:text-base font-medium">{option.text}</span>
              </div>
            ))}
          </div>
        );

      case 'multi_select': {
        const selected = Array.isArray(answers[question.id]) ? answers[question.id] : [];
        const min = question.min_selections;
        const max = question.max_selections;
        const toggleOption = (val) => {
          const isSelected = selected.includes(val);
          if (isSelected) {
            handleAnswer(question.id, selected.filter(v => v !== val));
          } else {
            if (max && selected.length >= max) {
              toast.error(`You can select up to ${max} option${max > 1 ? 's' : ''} only`);
              return;
            }
            handleAnswer(question.id, [...selected, val]);
          }
        };
        return (
          <div className="space-y-3 sm:space-y-4 max-w-lg mx-auto">
            {(min || max) && (
              <p className="text-center text-sm text-muted-foreground" data-testid="multi-select-rule">
                {min && max && min === max
                  ? `Choose exactly ${max}`
                  : min && max
                  ? `Choose between ${min} and ${max}`
                  : max
                  ? `Choose up to ${max}`
                  : `Choose at least ${min}`}
                {' '}<span className="font-medium text-foreground">({selected.length} selected)</span>
              </p>
            )}
            {question.options?.map((option) => {
              const isSelected = selected.includes(option.value);
              const isDisabled = !isSelected && max && selected.length >= max;
              return (
                <div
                  key={option.id}
                  className={`flex items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 ${
                    isSelected
                      ? 'border-secondary bg-gradient-to-r from-secondary/10 to-transparent shadow-md cursor-pointer'
                      : isDisabled
                      ? 'border-border opacity-50 cursor-not-allowed'
                      : 'border-border hover:border-secondary/50 hover:bg-secondary/5 cursor-pointer transform hover:scale-[1.02]'
                  }`}
                  onClick={() => toggleOption(option.value)}
                  data-testid={`multi-option-${option.value}`}
                >
                  <div className={`h-5 w-5 sm:h-6 sm:w-6 rounded border-2 flex items-center justify-center transition-all ${
                    isSelected ? 'border-secondary bg-secondary' : 'border-muted-foreground'
                  }`}>
                    {isSelected && <CheckSquare className="h-3 w-3 sm:h-4 sm:w-4 text-white" />}
                  </div>
                  <span className="text-sm sm:text-base font-medium">{option.text}</span>
                </div>
              );
            })}
          </div>
        );
      }

      case 'open_ended':
        return (
          <div className="max-w-lg mx-auto">
            <Textarea
              value={answers[question.id] || ''}
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              placeholder="Share your thoughts here..."
              data-testid="open-ended-input"
              className="min-h-[150px] resize-none"
            />
            <p className="text-xs text-muted-foreground mt-2 text-right">
              {(answers[question.id] || '').length} characters
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Survey Selection
  if (!selectedSurvey) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-background to-muted/30">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-border sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-secondary to-secondary/60 flex items-center justify-center text-white font-bold text-base sm:text-lg">
                {user?.name?.charAt(0) || 'E'}
              </div>
              <div>
                <h1 className="font-heading font-bold text-sm sm:text-lg">Welcome, {user?.name?.split(' ')[0]}</h1>
                <p className="text-xs sm:text-sm text-muted-foreground">{user?.department}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn" className="rounded-full text-xs sm:text-sm">
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Survey List */}
        <main className="flex-1 max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8 w-full">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">Your Voice Matters</h2>
            <p className="text-muted-foreground text-sm sm:text-base">Share your feedback to help us build a better workplace</p>
          </div>
          
          {surveys.length === 0 ? (
            <Card className="text-center py-12 sm:py-16 bg-white/60 backdrop-blur-sm">
              <CardContent>
                <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 sm:h-10 sm:w-10 text-muted-foreground" />
                </div>
                <h3 className="font-heading text-lg sm:text-xl font-medium mb-2">No surveys available</h3>
                <p className="text-muted-foreground text-sm">Check back later for new surveys</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {surveys.map((survey, idx) => (
                <Card 
                  key={survey.id}
                  className={`group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white/80 backdrop-blur-sm border-0 shadow-lg ${survey.is_completed ? 'opacity-60' : ''}`}
                  onClick={() => !survey.is_completed && loadSurveyDetails(survey.id)}
                  data-testid={`survey-card-${survey.id}`}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <CardContent className="p-0">
                    <div className="flex">
                      <div className={`w-1.5 sm:w-2 rounded-l-xl ${survey.is_completed ? 'bg-accent' : 'bg-gradient-to-b from-secondary to-secondary/60'}`}></div>
                      <div className="flex-1 p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0">
                          <div className="flex-1">
                            <h3 className="font-heading font-bold text-lg sm:text-xl mb-1 sm:mb-2 group-hover:text-secondary transition-colors">{survey.title}</h3>
                            <p className="text-muted-foreground text-sm mb-3 sm:mb-4 line-clamp-2">{survey.description}</p>
                            <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm">
                              <span className="flex items-center gap-1 sm:gap-2 text-primary">
                                <ListChecks className="h-3 w-3 sm:h-4 sm:w-4" />
                                <span className="font-medium">{survey.questions?.length || 0} Questions</span>
                              </span>
                              <span className="flex items-center gap-1 sm:gap-2 text-muted-foreground">
                                <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                                Due: {new Date(survey.end_date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          {survey.is_completed ? (
                            <div className="flex items-center gap-2 bg-accent/10 text-accent px-3 sm:px-4 py-1.5 sm:py-2 rounded-full self-start">
                              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              <span className="font-semibold text-sm">Completed</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 bg-secondary/10 text-secondary px-3 sm:px-4 py-1.5 sm:py-2 rounded-full group-hover:bg-secondary group-hover:text-white transition-all self-start">
                              <span className="font-semibold text-sm">Start Survey</span>
                              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
        <footer className="py-4 text-center bg-muted/30 border-t border-border" data-testid="global-footer">
          <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
          <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
        </footer>
      </div>
    );
  }

  // Survey Questions
  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress Bar */}
      <div className="sticky top-0 z-20 bg-card border-b border-border">
        <Progress value={progress} className="h-1 rounded-none" />
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              Question {currentQuestion + 1} of {questions.length}
            </span>
            {autoSaveStatus && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                autoSaveStatus === 'saved' ? 'bg-accent/10 text-accent' :
                autoSaveStatus === 'saving' ? 'bg-muted text-muted-foreground' :
                'bg-destructive/10 text-destructive'
              }`}>
                {autoSaveStatus === 'saved' ? '✓ Saved' :
                 autoSaveStatus === 'saving' ? 'Saving...' : 'Save failed'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Section Indicator */}
      {currentSection && sections.length > 1 && (
        <div className="bg-muted/50 border-b border-border py-2 px-4">
          <div className="max-w-2xl mx-auto flex items-center gap-2 overflow-x-auto">
            {sections.map((section, idx) => (
              <Badge 
                key={section}
                variant={section === currentSection ? "default" : "outline"}
                className={`flex items-center gap-1.5 whitespace-nowrap text-xs ${
                  section === currentSection 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-background'
                }`}
              >
                <FolderOpen className="h-3 w-3" />
                {section}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Question Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-2xl animate-fade-in" key={currentQuestion}>
          <Card className="shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <CardContent className="p-4 sm:p-8 md:p-12">
              {/* Section Label */}
              {currentSection && (
                <div className="flex justify-center mb-3">
                  <span className="text-xs text-muted-foreground flex items-center gap-1.5 bg-muted/50 px-3 py-1 rounded-full">
                    <FolderOpen className="h-3 w-3" />
                    {currentSection}
                  </span>
                </div>
              )}
              
              {/* Question Icon */}
              <div className="flex justify-center mb-4 sm:mb-6">
                <div className={`p-2.5 sm:p-3 rounded-full ${
                  currentQ.type === 'rating' ? 'bg-secondary/10 text-secondary' :
                  currentQ.type === 'yes_no' ? 'bg-accent/10 text-accent' :
                  currentQ.type === 'multiple_choice' ? 'bg-primary/10 text-primary' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {currentQ.type === 'rating' && <Star className="h-5 w-5 sm:h-6 sm:w-6" />}
                  {currentQ.type === 'yes_no' && <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6" />}
                  {currentQ.type === 'multiple_choice' && <ListChecks className="h-5 w-5 sm:h-6 sm:w-6" />}
                  {currentQ.type === 'multi_select' && <CheckSquare className="h-5 w-5 sm:h-6 sm:w-6" />}
                  {currentQ.type === 'open_ended' && <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6" />}
                </div>
              </div>

              {/* Question Text */}
              <h2 className="font-heading text-lg sm:text-xl md:text-2xl font-semibold text-center mb-6 sm:mb-8">
                {currentQ.text}
                {currentQ.is_required && <span className="text-destructive ml-1">*</span>}
              </h2>

              {/* Answer Options */}
              {renderQuestion(currentQ)}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Navigation */}
      <footer className="sticky bottom-0 bg-card border-t border-border z-50">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4 py-4 sm:py-5 px-4 sm:px-6">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestion(prev => prev - 1)}
            disabled={currentQuestion === 0}
            data-testid="prev-btn"
            className="px-4 py-2"
          >
            <ChevronLeft className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Back</span>
          </Button>

          {currentQuestion === questions.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-btn"
              className="bg-accent hover:bg-accent/90 px-4 py-2"
            >
              {submitting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1 sm:mr-2" />
                  Submit
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentQuestion(prev => prev + 1)}
              data-testid="next-btn"
              className="px-6 py-2"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1 sm:ml-2" />
            </Button>
          )}
        </div>
        <div className="text-center pt-3 border-t border-border mt-3" data-testid="global-footer">
          <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
          <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
        </div>
      </footer>
    </div>
  );
};

export default EmployeeSurvey;
